import { toNumber } from '../utils/number-operations';
import type {
  ApiInvestorFundRelationship,
  ApiInvestorFundSummary,
  ApiFundInvestorStatistics,
  ApiInvestorFundTransferAllocationResponse,
  ApiInvestorFundTransferResponse,
  InvestorFundRelationship,
  InvestorFundSummary,
  FundInvestorStatistics,
  InvestorFundTransferAllocationResponse,
  InvestorFundTransferResponse,
  InvestorDetail,
  PaginatedInvestorSummary,
  InvestorSummary,
} from '../types/investor';

function mapInvestorFundRelationship(
  raw: ApiInvestorFundRelationship
): InvestorFundRelationship {
  return {
    ...raw,
    commitment_amount: toNumber(raw.commitment_amount),
    total_called: toNumber(raw.total_called),
    total_contributed: toNumber(raw.total_contributed),
    total_distributed: toNumber(raw.total_distributed),
  };
}

function mapInvestorFundSummary(
  raw: ApiInvestorFundSummary
): InvestorFundSummary {
  return {
    ...raw,
    commitment_amount: toNumber(raw.commitment_amount),
  };
}

export function mapFundInvestorStatistics(
  raw: ApiFundInvestorStatistics
): FundInvestorStatistics {
  return {
    ...raw,
    total_commitments: toNumber(raw.total_commitments),
    total_contributed: toNumber(raw.total_contributed),
    total_distributed: toNumber(raw.total_distributed),
  };
}

function mapTransferAllocation(
  raw: ApiInvestorFundTransferAllocationResponse
): InvestorFundTransferAllocationResponse {
  return {
    ...raw,
    transferred_commitment_amount: toNumber(raw.transferred_commitment_amount),
    transferred_called_amount: toNumber(raw.transferred_called_amount),
    transferred_contributed_amount: toNumber(
      raw.transferred_contributed_amount
    ),
    transferred_distributed_amount: toNumber(
      raw.transferred_distributed_amount
    ),
    transferred_unfunded_commitment_amount: toNumber(
      raw.transferred_unfunded_commitment_amount
    ),
    transferred_cost_basis_amount: toNumber(raw.transferred_cost_basis_amount),
  };
}

export function mapInvestorFundTransferResponse(
  raw: ApiInvestorFundTransferResponse
): InvestorFundTransferResponse {
  return {
    ...raw,
    transferred_commitment_amount: toNumber(raw.transferred_commitment_amount),
    transferred_called_amount: toNumber(raw.transferred_called_amount),
    transferred_contributed_amount: toNumber(
      raw.transferred_contributed_amount
    ),
    transferred_distributed_amount: toNumber(
      raw.transferred_distributed_amount
    ),
    transferred_unfunded_commitment_amount: toNumber(
      raw.transferred_unfunded_commitment_amount
    ),
    transferred_cost_basis_amount: toNumber(raw.transferred_cost_basis_amount),
    allocations: raw.allocations.map(mapTransferAllocation),
  };
}

function mapFundRelationships(
  rels: (ApiInvestorFundSummary | ApiInvestorFundRelationship)[]
): (InvestorFundSummary | InvestorFundRelationship)[] {
  return rels.map((rel) => {
    if ('total_called' in rel) {
      return mapInvestorFundRelationship(rel as ApiInvestorFundRelationship);
    }
    return mapInvestorFundSummary(rel as ApiInvestorFundSummary);
  });
}

export function mapInvestorDetail(raw: InvestorDetail): InvestorDetail {
  return {
    ...raw,
    fund_relationships: mapFundRelationships(
      raw.fund_relationships as unknown as (
        ApiInvestorFundSummary | ApiInvestorFundRelationship
      )[]
    ) as InvestorFundRelationship[],
  };
}

function mapInvestorSummary(raw: InvestorSummary): InvestorSummary {
  return {
    ...raw,
    fund_relationships: mapFundRelationships(
      raw.fund_relationships as unknown as (
        ApiInvestorFundSummary | ApiInvestorFundRelationship
      )[]
    ) as InvestorFundSummary[],
  };
}

export function mapPaginatedInvestorSummary(
  raw: PaginatedInvestorSummary
): PaginatedInvestorSummary {
  return {
    ...raw,
    items: raw.items.map(mapInvestorSummary),
  };
}
